import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box,
  Text,
  HStack,
  Flex,
  IconButton,
  Image,
  Input,
  Spinner,
  FormControl,
  List,
  ListItem,
} from '@chakra-ui/react';
import { CloseIcon } from '@chakra-ui/icons';
import debounce from 'lodash/debounce';
import { BrandSearchResultDto } from '@foodfluence/shared';
import { brandApi } from '../../../../../../services/api/brand';
import type { LocalCreatorsBrandChip } from '../../types';
import { ff } from '../../../../../../theme';

export interface BrandsWorkedWithSectionProps {
  brands: LocalCreatorsBrandChip[];
  /** True while the marketplace profile (brand list) is first loading. */
  isProfileLoading: boolean;
  isLoadingBrandIcons: boolean;
  onAddBrand: (raw: string) => Promise<void>;
  onRemoveBrand: (brandName: string) => void;
}

const BrandsWorkedWithSection: React.FC<BrandsWorkedWithSectionProps> = ({
  brands,
  isProfileLoading,
  isLoadingBrandIcons,
  onAddBrand,
  onRemoveBrand,
}) => {
  const [brandInput, setBrandInput] = useState('');
  const [suggestions, setSuggestions] = useState<BrandSearchResultDto[]>([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [isAdding, setIsAdding] = useState(false);
  const [isSearchPending, setIsSearchPending] = useState(false);

  const fetchBrandSuggestions = useCallback(async (input: string) => {
    if (input.length < 2) {
      setSuggestions([]);
      setIsSearchPending(false);
      return;
    }
    setIsSearchPending(true);
    try {
      const results = await brandApi.searchBrands(input);
      setSuggestions(results);
      setShowSuggestions(true);
    } catch (error) {
      console.error('Error fetching brand suggestions:', error);
      setSuggestions([]);
    } finally {
      setIsSearchPending(false);
    }
  }, []);

  const debouncedFetchBrands = useMemo(
    () => debounce((input: string) => void fetchBrandSuggestions(input), 300),
    [fetchBrandSuggestions]
  );

  useEffect(() => {
    return () => {
      debouncedFetchBrands.cancel();
    };
  }, [debouncedFetchBrands]);

  const clearInput = useCallback(() => {
    setBrandInput('');
    setSuggestions([]);
    setShowSuggestions(false);
  }, []);

  const addBrand = useCallback(
    async (name: string) => {
      const raw = name.trim();
      if (!raw || isAdding || isLoadingBrandIcons) {
        return;
      }
      setIsAdding(true);
      try {
        await onAddBrand(raw);
        clearInput();
      } catch (e) {
        if ((e as Error)?.message === '__BRAND_DUPLICATE__') {
          clearInput();
        }
      } finally {
        setIsAdding(false);
      }
    },
    [clearInput, isAdding, isLoadingBrandIcons, onAddBrand]
  );

  const handleBrandNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setBrandInput(value);
    debouncedFetchBrands(value);
  };

  const handleSuggestionClick = (brand: BrandSearchResultDto) => {
    void addBrand(brand.name);
  };

  const handleEnterKey = () => {
    if (brandInput.trim().length > 0) {
      void addBrand(brandInput);
    }
  };

  return (
    <Box as="section" aria-label="Brands worked with">
      <HStack justify="space-between" align="center" mb="10px" spacing={2}>
        <Text as="h3" textStyle="eyebrow" color={ff.text2} flex={1} m={0}>
          Brands worked with
        </Text>
      </HStack>
      <Box
        border="1px solid"
        borderColor={ff.line}
        borderRadius="20px"
        overflow="visible"
        bg="white"
      >
        {isProfileLoading && (
          <Flex align="center" gap={2} p="16px" bg="white" borderTopRadius="20px">
            <Spinner size="sm" />
            <Text fontSize="13px" color={ff.text2}>
              Loading…
            </Text>
          </Flex>
        )}
        {!isProfileLoading && isLoadingBrandIcons && brands.length > 0 && (
          <Flex align="center" gap={2} p="16px" bg="white" borderTopRadius="20px">
            <Spinner size="sm" />
            <Text fontSize="13px" color={ff.text2}>
              Loading brand icons…
            </Text>
          </Flex>
        )}
        {!isProfileLoading && brands.length > 0 && !isLoadingBrandIcons && (
          <HStack spacing="8px" rowGap="8px" flexWrap="wrap" p="12px" bg="white" borderTopRadius="20px">
            {brands.map((b) => (
              <HStack
                key={b.id}
                h="36px"
                pl="6px"
                pr="2px"
                spacing="6px"
                borderRadius="full"
                bg={ff.grey}
              >
                <Box
                  position="relative"
                  w="24px"
                  h="24px"
                  borderRadius="full"
                  flexShrink={0}
                  overflow="hidden"
                  bg="white"
                >
                  {b.logoUrl ? (
                    <Image
                      src={b.logoUrl}
                      alt=""
                      w="100%"
                      h="100%"
                      objectFit="cover"
                      loading="lazy"
                      draggable={false}
                    />
                  ) : (
                    <Flex
                      w="100%"
                      h="100%"
                      align="center"
                      justify="center"
                      fontSize="11px"
                      fontWeight={600}
                      lineHeight="15px"
                      color={ff.ink}
                    >
                      {b.display.charAt(0)}
                    </Flex>
                  )}
                </Box>
                <Text
                  fontSize="14px"
                  fontWeight={500}
                  lineHeight="19.5px"
                  color={ff.ink}
                  noOfLines={1}
                  maxW="120px"
                >
                  {b.display}
                </Text>
                <IconButton
                  aria-label={`Remove ${b.display}`}
                  icon={<CloseIcon fontSize="9px" />}
                  w="32px"
                  h="32px"
                  minW="32px"
                  borderRadius="full"
                  color={ff.text2}
                  variant="ghost"
                  _hover={{ bg: 'white', color: ff.ink }}
                  onClick={() => onRemoveBrand(b.id)}
                />
              </HStack>
            ))}
          </HStack>
        )}

        {!isProfileLoading && (
          <FormControl position="relative" borderTop={brands.length > 0 ? '1px solid' : 'none'} borderColor={ff.line}>
            <Flex
              h="56px"
              px="16px"
              align="center"
              bg="white"
              borderBottomRadius="20px"
            >
              <Box position="relative" flex={1} minW={0} display="flex" alignItems="center">
                {isSearchPending && (
                  <Box position="absolute" right="4px" zIndex={2} pointerEvents="none">
                    <Spinner size="xs" color={ff.text2} />
                  </Box>
                )}
                {isAdding && (
                  <Box position="absolute" right="4px" zIndex={2} pointerEvents="none">
                    <Spinner size="xs" color={ff.text2} />
                  </Box>
                )}
                <Input
                  value={brandInput}
                  onChange={handleBrandNameChange}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      handleEnterKey();
                    }
                  }}
                  onFocus={() => {
                    if (brandInput.length >= 2 && suggestions.length > 0) {
                      setShowSuggestions(true);
                    }
                  }}
                  onBlur={() => {
                    setTimeout(() => setShowSuggestions(false), 200);
                  }}
                  pl="4px"
                  pr={isSearchPending || isAdding ? '28px' : '4px'}
                  fontSize="16px"
                  border="none"
                  p={0}
                  h="auto"
                  placeholder="Type to search brands, or paste a website URL"
                  _placeholder={{ color: ff.muted }}
                  _focusVisible={{ boxShadow: 'none' }}
                  flex={1}
                  aria-label="Search brand name or website URL"
                  isDisabled={isLoadingBrandIcons || isAdding}
                />
              </Box>
            </Flex>

            {showSuggestions && suggestions.length > 0 && (
              <List
                position="absolute"
                top="100%"
                left="12px"
                right="12px"
                bg="white"
                boxShadow="0 8px 24px rgba(14, 14, 14, 0.08)"
                borderRadius="14px"
                maxH="240px"
                overflowY="auto"
                zIndex={1500}
                mt="6px"
                border="1px solid"
                borderColor={ff.line}
                py="4px"
              >
                {suggestions.map((suggestion) => (
                  <ListItem
                    key={suggestion.name + (suggestion.icon ?? '')}
                    minH="48px"
                    py="8px"
                    px="12px"
                    cursor="pointer"
                    _hover={{ bg: ff.grey }}
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={() => handleSuggestionClick(suggestion)}
                    display="flex"
                    alignItems="center"
                    gap="10px"
                  >
                    {suggestion.icon && (
                      <Image
                        src={suggestion.icon}
                        alt=""
                        boxSize="28px"
                        borderRadius="8px"
                        objectFit="contain"
                        flexShrink={0}
                      />
                    )}
                    <Text fontWeight={500} fontSize="15px" color={ff.ink} noOfLines={2}>
                      {suggestion.name}
                    </Text>
                  </ListItem>
                ))}
              </List>
            )}
          </FormControl>
        )}
      </Box>
    </Box>
  );
};

export default BrandsWorkedWithSection;
